/**
 * MailRow — the shared row primitive for both inboxes (#626 · #631).
 *
 * Owns: cell rendering, read/unread weight, selection, star, and the hover
 * action strip. Does NOT own the grid template, the column set or which
 * actions exist — those come from the surface contract as DATA.
 *
 * **This component must never branch on `surface.kind`.** If a change here
 * needs to know which inbox it is in, widen `surfaceContract.js` instead. That
 * is the discipline that keeps one shared row from quietly becoming two.
 *
 * Read state is background + weight, never a dot: unread rows are white with a
 * heavier sender and subject; read rows are tinted `#FBFAF9` at regular
 * weight. #559's dot is gone, but the persisted `is_read` it introduced is
 * exactly what drives this — so this is a display change over shipped data,
 * not a re-fix.
 */
import React, { useState } from 'react';
import {
    ChevronsRight,
    Eye,
    MousePointer,
    Paperclip,
    Star,
} from 'lucide-react';

import { Checkbox } from '../ui/checkbox';
import { formatEmailDate } from './inboxUtils';
import { formatInZone, parseInstant } from '../../lib/timezone';
import { mailStateScopeHint, providerChip } from './originIdentity';
import { previewText } from './quotedBody';
import useDisplayZone from '../../hooks/useDisplayZone';
import { avatarTint, initials } from './avatars';
import { actionDef } from './mailRowState';

/** Display name for a row's sender.
 *
 * Provider syncs store the full `Name <addr>` string in `from_email` and the
 * bare address in `from_address`, so the name has to be recovered from
 * whichever of the two the row happens to carry.
 */
export function senderName(item) {
    if (item.from_name) return item.from_name;
    const raw = item.from_email || '';
    const stripped = raw.replace(/<[^>]*>/, '').trim();
    if (stripped) return stripped.replace(/^"|"$/g, '');
    return item.from_address || raw || 'Unknown sender';
}

export function senderAddress(item) {
    if (item.from_address) return item.from_address;
    const match = (item.from_email || '').match(/<([^>]+)>/);
    return match ? match[1] : item.from_email || '';
}

/** The provider label, from whichever field the row actually carries.
 *
 * `origin` is the N3 identity and the better source — it is resolved per
 * MESSAGE, so adjacent rows in a pooled inbox can name different providers.
 * But it is only on rows re-read since N3, so `_source` is the fallback: it is
 * on every row, and without it every historical row loses its chip.
 *
 * ONE chip, never two. Rendering `origin` and `_source` side by side produced
 * "Google Gmail" and "Outlook Outlook" on the personal list, where the design
 * draws a single pill.
 */
const SOURCE_LABELS = { gmail: 'Google', outlook: 'Outlook', crm: 'CRM' };

function providerLabel(mail) {
    return providerChip(mail.origin) || SOURCE_LABELS[mail._source] || null;
}

/** Open/click tracking on CRM-originated sends. Provider mail carries neither. */
function TrackingIcons({ mail }) {
    if (!mail.opened_at && !mail.clicked_at) return null;
    return (
        <>
            {mail.opened_at && (
                <Eye
                    className="w-3 h-3 text-green-500 flex-shrink-0"
                    aria-label="Opened"
                />
            )}
            {mail.clicked_at && (
                <MousePointer
                    className="w-3 h-3 text-purple-500 flex-shrink-0"
                    aria-label="Clicked"
                />
            )}
        </>
    );
}

/** How many messages the backend collapsed behind this row (#521).
 *
 * Absent on a row cached before the list returned conversations, which reads
 * as 1 — the same thing that row displayed before. Hidden at 1, where it would
 * be noise on most rows.
 */
function MessageCount({ mail }) {
    const count = mail.message_count || 1;
    if (count <= 1) return null;
    return (
        <span
            className="text-[11px] text-stone-500 flex-shrink-0 tabular-nums"
            data-testid={`inbox-message-count-${mail.comm_id}`}
            aria-label={`${count} messages in this conversation`}
        >
            {count}
        </span>
    );
}

function ProviderPill({ mail }) {
    const label = providerLabel(mail);
    if (!label) return null;
    return (
        <span className="text-[10px] leading-none bg-[#F1EFEA] text-stone-600 rounded-[4px] px-[5px] py-[1px] flex-shrink-0">
            {label}
        </span>
    );
}

function StarButton({ mail, onAction }) {
    const starred = mail.is_starred === true;
    // The hint names WHERE the change lands. A Gmail star is CRM-only — the
    // OAuth scope list excludes gmail.modify — and a control that silently
    // does less than it appears to is the defect class this whole contract
    // exists to prevent.
    const hint = mailStateScopeHint(mail.origin, starred ? 'Unstar' : 'Star');
    return (
        <button
            type="button"
            title={hint || (starred ? 'Unstar' : 'Star')}
            aria-label={starred ? 'Unstar' : 'Star'}
            aria-pressed={starred}
            data-testid="mail-row-star"
            onClick={(e) => {
                e.stopPropagation();
                onAction('star', mail, { starred: !starred });
            }}
            className="w-5 h-5 grid place-items-center text-stone-400 hover:text-amber-500"
        >
            <Star
                className={`w-4 h-4 ${starred ? 'fill-amber-400 text-amber-400' : ''}`}
            />
        </button>
    );
}

/**
 * The Important marker — the chevron Gmail draws beside the star.
 *
 * A CRM-OWNED flag, by decision. Gmail's `IMPORTANT` is a label Google
 * INFERS rather than one the user sets, writing it back would need
 * `gmail.modify` (excluded from the scope set), and Outlook has no equivalent
 * at all. Reading the provider would therefore give the same control three
 * different behaviours depending on where a message came from. This behaves
 * identically on Gmail, Outlook and CRM mail, and never leaves NurtureCRM.
 *
 * Deliberately NOT given a `mailStateScopeHint`: that hint exists to warn
 * that a change may not reach the real mailbox, and this one never claims to.
 */
function ImportantButton({ mail, onAction }) {
    const important = mail.is_important === true;
    return (
        <button
            type="button"
            title={important ? 'Not important' : 'Mark as important'}
            aria-label={important ? 'Not important' : 'Mark as important'}
            aria-pressed={important}
            data-testid="mail-row-important"
            onClick={(e) => {
                e.stopPropagation();
                onAction('important', mail, { important: !important });
            }}
            className="w-5 h-5 grid place-items-center text-stone-300 hover:text-amber-600"
        >
            <ChevronsRight
                className={`w-[15px] h-[15px] -rotate-45 ${
                    important ? 'text-amber-500' : ''
                }`}
                strokeWidth={important ? 3 : 2}
            />
        </button>
    );
}

function HoverActions({ mail, actions, onAction, folder }) {
    return (
        <div
            className="flex items-center gap-[2px]"
            data-testid="mail-row-actions"
            onClick={(e) => e.stopPropagation()}
        >
            {actions.map((key) => {
                const def = actionDef(key, mail, folder);
                if (!def) return null;
                const Icon = def.icon;
                return (
                    <button
                        key={key}
                        type="button"
                        title={def.title}
                        aria-label={def.title}
                        data-testid={`mail-row-action-${key}`}
                        data-action-id={
                            key === 'delete' ? `inbox-delete-${mail.comm_id}` : undefined
                        }
                        onClick={() => onAction(key, mail)}
                        className="w-[26px] h-[26px] rounded-[7px] grid place-items-center text-stone-600 hover:bg-stone-200"
                    >
                        <Icon className="w-[15px] h-[15px]" />
                    </button>
                );
            })}
        </div>
    );
}

export default function MailRow({
    mail,
    surface,
    selected = false,
    /**
     * Whether this viewer may select rows at all.
     *
     * A checkbox that leads to no action it can perform is worse than no
     * checkbox: on a shared inbox a `viewer` can read but cannot triage, and
     * offering them selection plus a bulk bar promises verbs the API will
     * refuse. The cell is kept (as an empty spacer) so the grid does not
     * reflow between roles.
     */
    selectable = true,
    onSelect,
    onOpen,
    onAction,
    /**
     * Optional cell renderers, as COMPOSITION rather than contract flags.
     *
     * `surfaceContract` stays data — it says which columns exist, never how to
     * draw them. But the unified list has two cells with real logic behind
     * them (recipient chips with overflow, and a four-state reply badge), and
     * moving that logic in here would either fork the row or smuggle behaviour
     * into the contract. So the surface passes the cell instead.
     */
    renderOwnerCell,
    renderMetaExtra,
    /** Forwarded to the row element, for the list's own roving focus. */
    /** Narrows `surface.rowActions` by what this viewer may actually do. */
    rowActions,
    /**
     * The VIEW this row is drawn in, which the action strip needs and cannot
     * read off the row. A pooled inbox's Trash is a per-inbox removal record,
     * not `mail.mail_folder`, so without this the strip in a unified Trash
     * says "Move to Trash" on a message already in it.
     */
    folder,
    rowRef,
    /** Runs after MailRow's own key handling — arrow-key navigation lives in
     *  the list, which is the only thing that knows about the other rows. */
    onKeyDown,
}) {
    const [hovered, setHovered] = useState(false);
    const zone = useDisplayZone();

    // Tri-state, matching the backend: an ABSENT `is_read` is read. Rows
    // written before read state existed must not all light up as unread.
    const unread = mail.is_read === false;
    const name = senderName(mail);
    const showChipOnSubject = surface.providerChipSlot === 'subject';
    const stamp = formatEmailDate(mail.comm_date, { zone });
    // §11.2 draws a CLOCK TIME on the row ("2:41 PM"), not "3h ago" — a mail
    // list is scanned against the working day, and a relative stamp makes two
    // messages an hour apart look equally recent. Older rows fall back to a
    // date. `formatEmailDate`'s own `relative` is still what the thread reader
    // and the detail page use, so this is a row-level presentation choice
    // rather than a change to the shared formatter.
    const sameDay =
        parseInstant(mail.comm_date) &&
        formatInZone(parseInstant(mail.comm_date), { zone, formatStr: 'yyyy-MM-dd' }) ===
            formatInZone(new Date(), { zone, formatStr: 'yyyy-MM-dd' });
    const rowStamp = stamp
        ? formatInZone(parseInstant(mail.comm_date), {
              zone,
              formatStr: sameDay ? 'h:mm a' : 'MMM d',
          })
        : '';

    return (
        <div
            role="button"
            tabIndex={0}
            aria-label={`Open email from ${name}`}
            // `inbox-item-<comm_id>` is the shipped spelling both list surfaces
            // and their tests address rows by; `data-mail-row` is the generic
            // marker for "any row", which this component's own tests use.
            data-testid={`inbox-item-${mail.comm_id}`}
            data-mail-row="true"
            data-unread={unread ? 'true' : 'false'}
            onClick={() => onOpen?.(mail)}
            ref={rowRef}
            onKeyDown={(e) => {
                // Enter/Space open the row. The prototype's markup is a div,
                // so without this the list is unreachable by keyboard — and a
                // `role="button"` that does not respond to Space is worse than
                // no role at all, because it promises the behaviour.
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen?.(mail);
                }
                onKeyDown?.(e);
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{ gridTemplateColumns: surface.gridTemplate }}
            className={`grid items-center gap-[14px] px-5 py-[11px] cursor-pointer
                        border-b border-[#F2F0EC] hover:bg-[#F6F5F1]
                        ${unread ? 'bg-white' : 'bg-[#FBFAF9]'}`}
        >
            <div onClick={(e) => e.stopPropagation()}>
                {selectable && (
                    <Checkbox
                        checked={selected}
                        aria-label={`Select ${name}`}
                        onCheckedChange={(checked) => onSelect?.(mail.comm_id, !!checked)}
                    />
                )}
            </div>

            <StarButton mail={mail} onAction={onAction} />

            <ImportantButton mail={mail} onAction={onAction} />

            <div className="flex items-center gap-2 min-w-0">
                <span
                    className={`w-7 h-7 rounded-full text-[11px] font-semibold grid place-items-center flex-shrink-0 ${avatarTint(name)}`}
                >
                    {initials(name, 1)}
                </span>
                <span className="min-w-0">
                    <span
                        className={`block truncate text-[13.5px] ${
                            unread
                                ? 'font-semibold text-stone-950'
                                : 'font-[450] text-stone-700'
                        }`}
                    >
                        {name}
                    </span>
                    <span className="block truncate text-[11.5px] text-[#A29B95]">
                        {senderAddress(mail)}
                    </span>
                </span>
            </div>

            {surface.columns.includes('owner') && (
                renderOwnerCell ? (
                    <div className="min-w-0">{renderOwnerCell(mail)}</div>
                ) : (
                <div className="flex items-center gap-2 min-w-0">
                    <span
                        className={`w-7 h-7 rounded-full text-[11px] font-semibold grid place-items-center flex-shrink-0 ${avatarTint(mail.mailbox_owner_name || mail.mailbox_address)}`}
                    >
                        {initials(mail.mailbox_owner_name || mail.mailbox_address)}
                    </span>
                    <span className="min-w-0">
                        <span className="block truncate text-[13.5px] text-stone-700">
                            {mail.mailbox_owner_name || '—'}
                        </span>
                        <span className="flex items-center gap-1 min-w-0">
                            <span className="truncate text-[11.5px] text-[#A29B95]">
                                {mail.mailbox_address}
                            </span>
                            <ProviderPill mail={mail} />
                        </span>
                    </span>
                </div>
                )
            )}

            <div className="min-w-0">
                <span className="flex items-center gap-[6px] min-w-0">
                    {showChipOnSubject && <ProviderPill mail={mail} />}
                    <span
                        className={`truncate text-[13.5px] ${
                            unread
                                ? 'font-semibold text-stone-950'
                                : 'font-[450] text-stone-700'
                        }`}
                    >
                        {mail.subject || '(No Subject)'}
                    </span>
                    <MessageCount mail={mail} />
                </span>
                <span className="block truncate text-[13px] text-[#A29B95]">
                    {/* The literal em dash is the prototype's own separator
                        between subject and snippet. */}
                    {`— ${previewText(mail.body_text || mail.body || '')}`}
                </span>
            </div>

            <div className="flex items-center justify-end gap-2 min-w-0">
                {hovered ? (
                    <HoverActions
                        mail={mail}
                        actions={rowActions || surface.rowActions}
                        folder={folder}
                        onAction={onAction}
                    />
                ) : (
                    <>
                        {surface.showTrackingIcons && <TrackingIcons mail={mail} />}
                        {mail.has_attachments && (
                            <Paperclip
                                className="w-[14px] h-[14px] text-stone-400 flex-shrink-0"
                                aria-label="Has attachment"
                            />
                        )}
                        {surface.showStatusBadge && (
                            renderMetaExtra
                                ? renderMetaExtra(mail)
                                : mail.status_label && (
                                      <span className="text-[11px] text-[#B45309] bg-[#FEF6EC] border border-[#F7E4C8] rounded-[4px] px-[5px] py-[1px] flex-shrink-0">
                                          {mail.status_label}
                                      </span>
                                  )
                        )}
                        {/* formatEmailDate returns {relative, full}: the row
                            shows the relative stamp, the title carries the
                            full one, which names its own zone (#532). */}
                        <span
                            className="text-[11.5px] text-[#A29B95] flex-shrink-0"
                            title={stamp?.full || undefined}
                        >
                            {rowStamp}
                        </span>
                    </>
                )}
            </div>
        </div>
    );
}
